import { AreaChart, BarChart } from '@mantine/charts';
import { Badge, Group, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import {
  applyLens,
  engagementOf,
  formatLensValue,
  indexSeries,
  LENS_UNITS,
  MIN_CLIENTS_FOR_PER100,
  describeChange,
  trendOf,
  type Lens,
} from './lenses';
import SectionCard from './SectionCard';
import {
  HIGH_USE_THRESHOLD,
  previousMonth,
  USAGE_PARTS,
  type Firm,
  type UsageModel,
} from './usageModel';

type Props = {
  model: UsageModel;
  month: string;
  lens: Lens;
  today: Date;
  onSelectFirm: (name: string) => void;
};

const MONTHS_SHOWN = 12;
const LEADERBOARD_SIZE = 5;
/** A firm has to have shown up for a while before "no usage this month" means anything. */
const ATTENTION_MIN_ACTIVE_MONTHS = 3;

function trailingMonths(model: UsageModel, month: string): string[] {
  const end = model.months.indexOf(month);
  const start = Math.max(0, end - (MONTHS_SHOWN - 1));
  return model.months.slice(start, end + 1);
}

function firmUsageUnderLens(
  firm: Firm,
  month: string,
  lens: Lens,
  model: UsageModel,
  today: Date,
): number | null {
  const usage = firm.series.get(month)?.usage ?? 0;
  return applyLens(usage, month, lens, { model, today, firm });
}

/** Ranking lenses need a client floor; the raw and per-day views show every firm. */
function eligibleFirms(model: UsageModel, month: string, lens: Lens): Firm[] {
  if (lens !== 'per100') return model.firms;
  return model.firms.filter(firm => (firm.clientsAt.get(month) ?? 0) >= MIN_CLIENTS_FOR_PER100);
}

type SnapshotRow = {
  label: string;
  current: number;
  previous: number;
  total?: 'subtotal' | 'total';
};

type SnapshotGroup = { title: string; rows: SnapshotRow[] };

const TOTAL_ROW_STYLES: Record<'subtotal' | 'total', React.CSSProperties> = {
  subtotal: {
    borderTop: '1px solid var(--mantine-color-default-border)',
    fontWeight: FONT_WEIGHTS.semibold,
  },
  total: {
    background: 'var(--mantine-color-default-hover)',
    borderTop: '1px solid var(--mantine-color-default-border)',
    fontWeight: FONT_WEIGHTS.semibold,
  },
};

function perActiveFirm(usage: number, activeFirms: number): number {
  return activeFirms > 0 ? Math.round(usage / activeFirms) : 0;
}

const CHANGE_COLORS = { up: 'green', down: 'red', flat: 'gray', new: 'blue' } as const;

function ChangeBadge({ current, previous }: { current: number; previous: number }) {
  const change = describeChange(current, previous);
  if (change.direction === 'none') {
    return (
      <Text size="textSm" c="dimmed">
        {change.label}
      </Text>
    );
  }
  return (
    <Badge size="sm" variant="light" color={CHANGE_COLORS[change.direction]}>
      {change.label}
    </Badge>
  );
}

function SnapshotTable({
  groups,
  currentLabel,
  previousLabel,
}: {
  groups: SnapshotGroup[];
  currentLabel: string;
  previousLabel: string | null;
}) {
  const columns = previousLabel ? 4 : 2;
  return (
    <Table
      verticalSpacing={6}
      withRowBorders={false}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      <Table.Thead>
        <Table.Tr style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
          <Table.Th />
          <Table.Th ta="right">{currentLabel}</Table.Th>
          {previousLabel && <Table.Th ta="right">{previousLabel}</Table.Th>}
          {previousLabel && <Table.Th ta="right">Change</Table.Th>}
        </Table.Tr>
      </Table.Thead>
      {groups.map(group => (
        <Table.Tbody key={group.title}>
          <Table.Tr>
            <Table.Td colSpan={columns} pt="md" pb={4}>
              <Text size="textXs" c="dimmed" tt="uppercase" fw={FONT_WEIGHTS.semibold}>
                {group.title}
              </Text>
            </Table.Td>
          </Table.Tr>
          {group.rows.map(row => (
            <Table.Tr key={row.label} style={row.total ? TOTAL_ROW_STYLES[row.total] : undefined}>
              <Table.Td>{row.label}</Table.Td>
              <Table.Td ta="right">{row.current.toLocaleString('en-GB')}</Table.Td>
              {previousLabel && (
                <Table.Td ta="right" c={row.total ? undefined : 'dimmed'}>
                  {row.previous.toLocaleString('en-GB')}
                </Table.Td>
              )}
              {previousLabel && (
                <Table.Td ta="right">
                  <ChangeBadge current={row.current} previous={row.previous} />
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      ))}
    </Table>
  );
}

function TrendBadge({ trend }: { trend: string }) {
  const colours: Record<string, string> = {
    Up: 'green',
    Down: 'red',
    Flat: 'gray',
    New: 'blue',
  };
  return (
    <Badge size="sm" variant="light" color={colours[trend] ?? 'gray'}>
      {trend}
    </Badge>
  );
}

export default function OverviewPanel({ model, month, lens, today, onSelectFirm }: Props) {
  const tail = trailingMonths(model, month);
  const prev = previousMonth(model, month);
  const platform = model.platform.get(month);
  const unit = LENS_UNITS[lens];

  // Charts use the non-ranking lenses only; share is a per-firm idea, so it falls back to raw.
  const chartLens: Lens = lens === 'share' ? 'raw' : lens;

  // Both usage lines are built the same way, so they can't drift apart under a lens.
  const platformSeries = (field: 'usage' | 'abc') => {
    const raw = tail.map(key =>
      applyLens(
        model.platform.get(key)?.[field] ?? 0,
        key,
        chartLens === 'indexed' ? 'raw' : chartLens,
        { model, today },
      ),
    );
    return chartLens === 'indexed' ? indexSeries(raw) : raw;
  };
  const usageSeries = platformSeries('usage');
  const abcSeries = platformSeries('abc');

  const rawClients = tail.map(key => model.cumulativeClients.get(key) ?? 0);
  const clientSeries = chartLens === 'indexed' ? indexSeries(rawClients) : rawClients;

  const usageData = tail.map((key, index) => ({
    month: model.monthLabels.get(key) ?? key,
    usage: usageSeries[index],
    abc: abcSeries[index],
  }));

  const clientData = tail.map((key, index) => ({
    month: model.monthLabels.get(key) ?? key,
    clients: clientSeries[index],
  }));

  const engagementData = tail.map(key => ({
    month: model.monthLabels.get(key) ?? key,
    active: model.activeFirms.get(key) ?? 0,
    high: model.highUseFirms.get(key) ?? 0,
  }));

  const previousCounts = prev ? model.platform.get(prev) : undefined;
  const pair = (key: keyof Firm['totals']) => ({
    current: platform?.[key] ?? 0,
    previous: previousCounts?.[key] ?? 0,
  });
  const activeNow = model.activeFirms.get(month) ?? 0;
  const activeBefore = prev ? (model.activeFirms.get(prev) ?? 0) : 0;
  const snapshotGroups: SnapshotGroup[] = platform
    ? [
        {
          title: 'Actions by customers (ABC)',
          rows: [
            ...USAGE_PARTS.map(({ key, label }) => ({ label, ...pair(key) })),
            { label: 'ABC excluding adding new clients', ...pair('usage'), total: 'subtotal' },
            { label: 'New clients added', ...pair('newClients') },
            { label: 'Total ABC including adding new clients', ...pair('abc'), total: 'total' },
          ],
        },
        {
          title: 'Engagement',
          rows: [
            { label: 'Active firms', current: activeNow, previous: activeBefore },
            {
              label: 'Actions per active firm',
              current: perActiveFirm(platform.usage, activeNow),
              previous: perActiveFirm(previousCounts?.usage ?? 0, activeBefore),
            },
          ],
        },
      ]
    : [];
  const monthLabel = model.monthLabels.get(month) ?? month;
  const previousLabel = prev ? (model.monthLabels.get(prev) ?? prev) : null;
  const snapshotSubtitle = [
    previousLabel
      ? `${monthLabel} compared with ${previousLabel}`
      : `${monthLabel} · no earlier month to compare with`,
    ...(month === model.partialMonth ? ['month in progress'] : []),
  ].join(' · ');

  const ranked = eligibleFirms(model, month, lens)
    .map(firm => ({ firm, value: firmUsageUnderLens(firm, month, lens, model, today) }))
    .filter(
      (entry): entry is { firm: Firm; value: number } => entry.value !== null && entry.value > 0,
    )
    .sort((a, b) => b.value - a.value);

  const leaders = ranked.slice(0, LEADERBOARD_SIZE);

  const movers = eligibleFirms(model, month, lens)
    .map(firm => {
      const current = firmUsageUnderLens(firm, month, lens, model, today) ?? 0;
      const previous = prev ? (firmUsageUnderLens(firm, prev, lens, model, today) ?? 0) : 0;
      return { firm, current, previous, delta: current - previous };
    })
    .filter(entry => entry.current > 0 || entry.previous > 0)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, LEADERBOARD_SIZE);

  const attention = model.firms.filter(firm => {
    const counts = firm.series.get(month);
    return firm.activeMonths >= ATTENTION_MIN_ACTIVE_MONTHS && (counts?.usage ?? 0) === 0;
  });

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <SectionCard
          title="Total actions by customers (ABC)"
          subtitle={`Per month, with and without adding new clients · last ${tail.length} months`}
        >
          <AreaChart
            h={220}
            data={usageData}
            dataKey="month"
            series={[
              { name: 'abc', label: `Including adding new clients${unit}`, color: 'orange.8' },
              { name: 'usage', label: `Excluding adding new clients${unit}`, color: 'orange.4' },
            ]}
            withLegend
            curveType="linear"
            withDots={false}
            connectNulls={false}
          />
        </SectionCard>

        <SectionCard
          title="Total clients"
          subtitle={`Clients on the platform, cumulative · last ${tail.length} months`}
        >
          <AreaChart
            h={220}
            data={clientData}
            dataKey="month"
            series={[{ name: 'clients', label: 'Total clients', color: 'blue.6' }]}
            curveType="linear"
            withDots={false}
          />
        </SectionCard>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <SectionCard
          title="Firm engagement"
          subtitle={`Active firms vs firms with more than ${HIGH_USE_THRESHOLD} actions (ABC excluding adding new clients)`}
          fill
        >
          <BarChart
            h="100%"
            mih={220}
            data={engagementData}
            dataKey="month"
            series={[
              { name: 'active', label: 'Active firms', color: 'brand.3' },
              { name: 'high', label: `>${HIGH_USE_THRESHOLD} actions`, color: 'brand.6' },
            ]}
          />
        </SectionCard>

        <SectionCard title="Usage snapshot" subtitle={snapshotSubtitle}>
          <SnapshotTable
            groups={snapshotGroups}
            currentLabel={monthLabel}
            previousLabel={previousLabel}
          />
        </SectionCard>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <SectionCard
          title="Client leaderboard"
          subtitle={`Top ${LEADERBOARD_SIZE} by usage in ${model.monthLabels.get(month)} · client imports excluded${
            lens === 'per100' ? ` · firms with ${MIN_CLIENTS_FOR_PER100}+ clients only` : ''
          }`}
        >
          <Table highlightOnHover verticalSpacing="xs">
            <Table.Tbody>
              {leaders.map((entry, index) => (
                <Table.Tr
                  key={entry.firm.name}
                  onClick={() => onSelectFirm(entry.firm.name)}
                  style={{ cursor: 'pointer' }}
                >
                  <Table.Td w={28}>
                    <Text size="textXs" c="dimmed">
                      {index + 1}
                    </Text>
                  </Table.Td>
                  <Table.Td>{entry.firm.name}</Table.Td>
                  <Table.Td ta="right" fw={FONT_WEIGHTS.semibold}>
                    {formatLensValue(entry.value, lens)}
                  </Table.Td>
                </Table.Tr>
              ))}
              {leaders.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={3}>
                    <Text size="textXs" c="dimmed">
                      No firms with usage this month.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </SectionCard>

        <SectionCard
          title="Biggest movers"
          subtitle={`Change in usage vs ${prev ? model.monthLabels.get(prev) : '—'}${
            month === model.partialMonth
              ? ' · a part-month against a full one, so declines dominate'
              : ''
          }`}
        >
          <Table highlightOnHover verticalSpacing="xs">
            <Table.Tbody>
              {movers.map(entry => (
                <Table.Tr
                  key={entry.firm.name}
                  onClick={() => onSelectFirm(entry.firm.name)}
                  style={{ cursor: 'pointer' }}
                >
                  <Table.Td>{entry.firm.name}</Table.Td>
                  <Table.Td ta="right">
                    <Text size="textSm" c={entry.delta >= 0 ? 'green' : 'red'}>
                      {entry.delta >= 0 ? '+' : ''}
                      {formatLensValue(entry.delta, lens)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
              {movers.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={2}>
                    <Text size="textXs" c="dimmed">
                      Nothing moved this month.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </SectionCard>
      </SimpleGrid>

      <SectionCard
        title="Firms needing attention"
        subtitle={`${ATTENTION_MIN_ACTIVE_MONTHS} or more active months, no usage in ${model.monthLabels.get(month)}${
          month === model.partialMonth
            ? ' · expected this early in a part-month, read with care'
            : ''
        }`}
      >
        <Table highlightOnHover verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Firm</Table.Th>
              <Table.Th ta="right">Active months</Table.Th>
              <Table.Th ta="right">Last active</Table.Th>
              <Table.Th ta="right">Engagement</Table.Th>
              <Table.Th ta="right">Trend</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {attention.map(firm => {
              const counts = firm.series.get(month);
              return (
                <Table.Tr
                  key={firm.name}
                  onClick={() => onSelectFirm(firm.name)}
                  style={{ cursor: 'pointer' }}
                >
                  <Table.Td>{firm.name}</Table.Td>
                  <Table.Td ta="right">{firm.activeMonths}</Table.Td>
                  <Table.Td ta="right">{model.monthLabels.get(firm.last)}</Table.Td>
                  <Table.Td ta="right">
                    <Badge size="sm" variant="light" color="gray">
                      {engagementOf(counts?.usage ?? 0, counts?.newClients ?? 0)}
                    </Badge>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Group justify="flex-end" gap={4}>
                      <TrendBadge trend={trendOf(firm, month, model)} />
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
            {attention.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text size="textXs" c="dimmed">
                    Every established firm used the product this month.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </SectionCard>
    </Stack>
  );
}
